/**
 * DEV-016C1 · Attention Centre resolution-route helpers for the
 * Operations Notification Centre.
 *
 * Kept as a pure standalone module so a deterministic Jest test
 * can prove the sanitisation contract without booting React /
 * react-router / SWR / the entire Notification Centre page.
 *
 * `safeAttentionRoute` accepts ONLY the canonical internal path
 * `/operations/attention?open=<id>` (optionally prefixed by a
 * same-origin absolute URL). It returns a router-safe internal
 * path string, or `null` when the input is missing, external,
 * malformed or points at a different route. The caller feeds the
 * result to react-router's INTERNAL navigator — never to an anchor.
 */

const _ATTENTION_PATH_RE =
    /^\/operations\/attention\?open=([A-Za-z0-9._-]{6,})$/;

function _permittedHosts() {
    const hosts = new Set();
    const backend = (typeof process !== "undefined"
        && process.env && process.env.REACT_APP_BACKEND_URL) || "";
    try {
        if (backend) hosts.add(new URL(backend).host.toLowerCase());
    } catch { /* ignored */ }
    try {
        if (typeof window !== "undefined" && window.location) {
            hosts.add(window.location.host.toLowerCase());
        }
    } catch { /* ignored */ }
    return hosts;
}

export function safeAttentionRoute(rawLink) {
    if (!rawLink || typeof rawLink !== "string") return null;
    let path = rawLink.trim();
    if (!path) return null;
    // Any absolute URL — accept ONLY when the host matches a
    // permitted origin, then strip the origin. Protocol-relative
    // (`//host/…`), `javascript:`, `data:`, `mailto:` are rejected.
    if (/^https?:\/\//i.test(path)) {
        try {
            const u = new URL(path);
            const hosts = _permittedHosts();
            if (hosts.size > 0 && !hosts.has(u.host.toLowerCase())) return null;
            path = u.pathname + (u.search || "");
        } catch {
            return null;
        }
    } else if (path.startsWith("//") || /^[a-z][a-z0-9+.-]*:/i.test(path)) {
        // Protocol-relative URL or non-http scheme (javascript:, data:, mailto:, ...).
        return null;
    }
    const m = path.match(_ATTENTION_PATH_RE);
    if (!m) return null;
    return `/operations/attention?open=${m[1]}`;
}

export function isWorkflowIntelligence(source) {
    if (!source || typeof source !== "object") return false;
    const domain = source?.metadata?.domain
        || source?.event?.metadata?.domain;
    if (domain === "workflow_intelligence") return true;
    const key = source.event_type || source.event_key
        || source.template_key || source?.event?.event_type;
    return typeof key === "string" && key.startsWith("workflow_exception_");
}
